// /src/ui/components/ModelBrowser.tsx

import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import { useModelStore, MAIN_SUBMODEL_ID } from '@/store/modelStore';

export default function ModelBrowser() {
  const uiMode = useModelStore((s) => s.uiMode);
  const setUiMode = useModelStore((s) => s.setUiMode);

  const parts = useModelStore((s) => s.parts);
  const instances = useModelStore((s) => s.instances);                       
  const selectedId = useModelStore((s) => s.selectedId);
  const setSelected = useModelStore((s) => s.setSelected);

  const currentStep = useModelStore((s) => s.currentStep);
  const viewStep = useModelStore((s) => s.viewStep);
  const setCurrentStep = useModelStore((s) => s.setCurrentStep);
  const setViewStep = useModelStore((s) => s.setViewStep);

  const submodels = useModelStore((s) => s.submodels);
  const activeSubmodelId = useModelStore((s) => s.activeSubmodelId);
  const setActiveSubmodel = useModelStore((s) => s.setActiveSubmodel);
  const createSubmodel = useModelStore((s) => s.createSubmodel);
  const updateSubmodel = useModelStore((s) => s.updateSubmodel);
  const deleteSubmodel = useModelStore((s) => s.deleteSubmodel);
  const addInstance = useModelStore((s) => s.addInstance);

  const pushToast = useModelStore((s) => s.pushToast);

  const [newName, setNewName] = useState('Submodel');
  const [nameDrafts, setNameDrafts] = useState<Record<string, string>>({});

  const contextId = useMemo(() => activeSubmodelId ?? MAIN_SUBMODEL_ID, [activeSubmodelId]);

  const filteredParts = useMemo(() => {
    return parts.filter((p) => (p.submodelId ?? MAIN_SUBMODEL_ID) === contextId);
  }, [parts, contextId]);

  const filteredInstances = useMemo(() => {
    return instances.filter((i) => (i.parentSubmodelId ?? MAIN_SUBMODEL_ID) === contextId);
  }, [instances, contextId]);

  const stepCounts = useMemo(() => {
    const map = new Map<number, number>();
    for (const p of filteredParts) {
      map.set(p.step, (map.get(p.step) ?? 0) + 1);
    }
    for (const i of filteredInstances) {
      map.set(i.step, (map.get(i.step) ?? 0) + 1);
    }
    return map;
  }, [filteredParts, filteredInstances]);

  const maxStepInModel = useMemo(() => {
    return Math.max(
      1,
      ...filteredParts.map((p) => p.step ?? 1),
      ...filteredInstances.map((i) => i.step ?? 1)
    );
  }, [filteredParts, filteredInstances]);

  const stepListMax = useMemo(() => Math.max(currentStep, maxStepInModel), [currentStep, maxStepInModel]);
  const viewShown = useMemo(() => Math.min(viewStep, stepListMax), [viewStep, stepListMax]);

  const activeSubmodelName = useMemo(() => {
    if (!activeSubmodelId) return 'Main model';
    return submodels.find((sm) => sm.id === activeSubmodelId)?.name ?? activeSubmodelId;
  }, [activeSubmodelId, submodels]);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="panelHeader">
        <div>
          <div className="title">Model</div>
          <div className="hint">Submodels, steps, and instruction mode.</div>
        </div>
      </div>

      <div className="panelBody">
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <div className="muted">
              <b>View:</b> <span className="mono">{activeSubmodelName}</span>
            </div>

            <div className="seg" title="Workflow">
              <button className={clsx(uiMode === 'edit' && 'active')} onClick={() => setUiMode('edit')}>
                Edit
              </button>
              <button
                className={clsx(uiMode === 'instructions' && 'active')}
                onClick={() => setUiMode('instructions')}
              >
                Steps
              </button>
            </div>
          </div>

          <div className="hr" />

          <div className="muted">
            <b>Submodel filter:</b>{' '}
            <span className="mono">{contextId}</span>
          </div>

          <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className={clsx('btn', !activeSubmodelId && 'primary')} onClick={() => setActiveSubmodel(null)}>
              Main
            </button>
            {submodels.filter((sm) => sm.id !== MAIN_SUBMODEL_ID).map((sm) => (
              <button
                key={sm.id}
                className={clsx('btn', activeSubmodelId === sm.id && 'primary')}
                onClick={() => setActiveSubmodel(sm.id)}
                title={`Filter to ${sm.name}`}
              >
                {sm.name}
              </button>
            ))}
          </div>
        </div>

        <div style={{ height: 12 }} />

        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <div>
              <div style={{ fontSize: 13 }}>Instances</div>
              <div className="muted">Place submodels into this context (MPD-style).</div>
            </div>
          </div>
          <div className="hr" />

          <div className="muted">
            <b>Insert:</b> creates a reference (Type 1 line) to a submodel file.
          </div>

          <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {submodels
              .filter((sm) => sm.id !== contextId)
              .filter((sm) => sm.id !== MAIN_SUBMODEL_ID || contextId !== MAIN_SUBMODEL_ID)
              .filter((sm) => sm.id !== MAIN_SUBMODEL_ID) // don't insert main into things
              .map((sm) => (
                <button
                  key={sm.id}
                  className="btn"
                  onClick={() => {
                    const id = addInstance({ submodelId: sm.id, parentSubmodelId: contextId, step: currentStep });
                    pushToast({ title: 'Inserted instance', detail: `${sm.name} (${id})`, kind: 'ok' });
                  }}
                  title={`Insert ${sm.name}`}
                  disabled={uiMode !== 'edit'}
                >
                  Insert {sm.name}
                </button>
              ))}
          </div>

          {filteredInstances.length > 0 && (
            <>
              <div style={{ height: 12 }} />
              <div className="list">
                {filteredInstances.map((ins) => {
                  const smName = submodels.find((s) => s.id === ins.submodelId)?.name ?? ins.submodelId;
                  return (
                    <div key={ins.id} className="modelRow">
                      <div>
                        <div className="rowTitle">
                          Instance <span className="mono">{ins.id}</span>
                        </div>
                        <div className="rowMeta">
                          <span className="muted">submodel:</span> <span className="mono">{smName}</span> ·{' '}
                          <span className="muted">step:</span> <span className="mono">{ins.step}</span>
                        </div>
                      </div>
                      <div className="rowActions">
                        <button className="btnSmall" onClick={() => setSelected(ins.id)}>
                          Select
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div style={{ height: 12 }} />

        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <div>
              <div style={{ fontSize: 13 }}>Submodels</div>
              <div className="muted">Organize builds into named groups (not a full MPD tree yet).</div>
            </div>
          </div>

          <div className="hr" />

          <div className="field">
            <label>New</label>
            <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Submodel name…" />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn primary"
              onClick={() => {
                const id = createSubmodel(newName);
                pushToast({ title: 'Created submodel', detail: `${newName} (${id})`, kind: 'ok' });
              }}
            >
              Create
            </button>
          </div>

          <div style={{ height: 12 }} />

          <div className="list">
            {submodels.map((sm) => (
              <div key={sm.id} className="modelRow">
                <div>
                  <div className="rowTitle">
                    <span className="mono">{sm.id}</span>
                    {sm.id === MAIN_SUBMODEL_ID && <span className="badge" style={{ marginLeft: 8 }}>main</span>}
                  </div>
                  <div className="rowMeta">
                    <input
                      value={nameDrafts[sm.id] ?? sm.name}
                      onChange={(e) =>
                        setNameDrafts((d) => ({ ...d, [sm.id]: e.target.value }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
                        if (e.key === 'Escape') {
                          setNameDrafts((d) => {
                            const next = { ...d };
                            delete next[sm.id];
                            return next;
                          });
                          (e.currentTarget as HTMLInputElement).blur();
                        }
                      }}
                      onBlur={() => {
                        const raw = (nameDrafts[sm.id] ?? sm.name).trim();
                        const nextName = raw || sm.name;
                        if (nextName !== sm.name && uiMode === 'edit') {
                          updateSubmodel(sm.id, { name: nextName });
                        }
                        setNameDrafts((d) => {
                          const next = { ...d };
                          delete next[sm.id];
                          return next;
                        });
                      }}
                      disabled={uiMode !== 'edit'}
                      style={{
                        width: '100%',
                        marginTop: 6,
                        padding: '7px 8px',
                        borderRadius: 10,
                        border: '1px solid var(--border)',
                        background: 'var(--input-bg)',
                        color: 'var(--text)',
                      }}
                    />
                  </div>
                </div>

                <div className="rowActions">
                  <button className="btnSmall" onClick={() => setActiveSubmodel(sm.id)} title="Filter to this submodel">
                    Show
                  </button>
                  {sm.id !== MAIN_SUBMODEL_ID && (
                    <button
                      className="btnSmall danger"
                      onClick={() => {
                        deleteSubmodel(sm.id);
                        pushToast({ title: 'Deleted submodel', detail: sm.name, kind: 'warn' });
                      }}
                      title="Delete submodel (parts move to Main model)"
                      disabled={uiMode !== 'edit'}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ height: 12 }} />

        <div className="card">
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 13 }}>Steps</div>
              <div className="muted">Instruction mode uses View step to reveal the build gradually.</div>
            </div>
            <span className="badge">
              context max: <span className="mono">{maxStepInModel}</span>
            </span>
          </div>

          <div className="hr" />

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <div className="badge">
              view: <span className="mono">{viewShown}</span>
            </div>
            <div className="badge">
              current: <span className="mono">{currentStep}</span>
            </div>
          </div>

          <div style={{ height: 10 }} />

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              className="btn"
              onClick={() => setViewStep(Math.max(1, viewShown - 1))}
              disabled={viewShown <= 1}
            >
              Prev view
            </button>
            <button
              className="btn"
              onClick={() => setViewStep(Math.max(1, viewShown + 1))}
              disabled={viewShown >= stepListMax}
            >
              Next view
            </button>

            <div style={{ flex: 1 }} />

            <button
              className="btn"
              onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
              disabled={uiMode !== 'edit' || currentStep <= 1}
              title="Sets the step new parts will be added to"
            >
              Current −
            </button>
            <button
              className="btn"
              onClick={() => setCurrentStep(currentStep + 1)}
              disabled={uiMode !== 'edit'}
              title="Sets the step new parts will be added to"
            >
              Current +
            </button>
          </div>

          <div style={{ height: 12 }} />

          <div className="list">
            {Array.from({ length: Math.max(1, stepListMax) }, (_, i) => i + 1).map((s) => {
              const count = stepCounts.get(s) ?? 0;
              return (
                <div
                  key={s}
                  className="modelRow"
                  style={{
                    opacity: count === 0 ? 0.6 : 1,
                    borderColor: s === viewStep ? 'rgba(124, 92, 255, 0.55)' : undefined,
                  }}
                >
                  <div>
                    <div className="rowTitle">
                      Step <span className="mono">{s}</span>
                      <span className="badge" style={{ marginLeft: 8 }}>
                        {count} items
                      </span>
                    </div>
                    <div className="rowMeta">
                      Click “Show” to set view step. In Instruction mode, camera frames this step.
                    </div>
                  </div>
                  <div className="rowActions">
                    <button
                      className="btnSmall"
                      onClick={() => {
                        setViewStep(s);
                        // Select first part in that step (filtered by submodel filter)
                        const p = filteredParts.find((pp) => pp.step === s);
                        if (p) setSelected(p.id);
                      }}
                    >
                      Show
                    </button>
                    {selectedId && (
                      <button
                        className="btnSmall"
                        onClick={() => setSelected(null)}
                        title="Clear selection"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ height: 16 }} />

        <div className="muted">
          <b>Note:</b> Submodels now behave like MPD subfiles. Use <b>Instances</b> to place submodels into
          the current context.
        </div>
      </div>
    </div>
  );
}
